import React, { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, Navigate, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { store } from './store';
import EditorLayout from './layouts/EditorLayout';
import ErrorBoundary from './components/common/ErrorBoundary';
import LoginScreen from './components/auth/LoginScreen';
import FloorPlanLanding from './components/landing/FloorPlanLanding';
import ProfilePage from './components/profile/ProfilePage';
import { useAppDispatch, useAppSelector } from './hooks/redux';
import { loginSuccess } from './features/auth/authSlice';
import { refreshAccessToken } from './utils/http';
import {
  restoreSession,
  __resetSessionRestoreForTests,
} from './utils/sessionRestore';
import { PoweredByPathfinder } from './components/common/PoweredByPathfinder';
import './index.css';

export { __resetSessionRestoreForTests };

const SessionBootstrap: React.FC = () => (
  <div className="flex min-h-screen flex-col bg-[#0b1220]">
    <div className="flex flex-1 items-center justify-center text-sm text-slate-300">
      Checking session…
    </div>
    <footer className="flex h-8 shrink-0 items-center border-t border-slate-800 bg-[#0b1220] px-4 text-[11px] text-slate-400">
      <PoweredByPathfinder />
    </footer>
  </div>
);

/**
 * AppRoutes - Handles authenticated user routes
 * Routes between the floor plan landing page and editor layout based on floor plan state
 */
const AppRoutes: React.FC = () => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const hasFloorPlan = useAppSelector((state) => !!state.floorplan.name);
  const location = useLocation();
  const navigate = useNavigate();

  // Access token is memory-only; on load try HttpOnly refresh cookie once.
  // Success → landing (or editor if a floor plan is already open).
  // Failure / no cookie → sign-in. Never show landing until login/restore succeeds.
  const [bootstrapping, setBootstrapping] = useState(() => !isAuthenticated);

  useEffect(() => {
    if (!bootstrapping) return;

    let cancelled = false;
    void restoreSession(refreshAccessToken).then((result) => {
      if (cancelled) return;
      if (result?.accessToken) {
        dispatch(
          loginSuccess({
            username: result.username,
            accessToken: result.accessToken,
            tokenType: result.tokenType,
            userId: result.userId,
          })
        );
        // Prefer landing root after a silent restore (except for deep links under /profile).
        if (location.pathname !== '/' && location.pathname !== '/profile') {
          navigate('/', { replace: true });
        }
      }
      setBootstrapping(false);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the URL on `/` while logged out so the next successful login opens landing.
  useEffect(() => {
    if (bootstrapping) return;
    if (!isAuthenticated && location.pathname !== '/') {
      navigate('/', { replace: true });
    }
  }, [bootstrapping, isAuthenticated, location.pathname, navigate]);

  if (bootstrapping) {
    return <SessionBootstrap />;
  }

  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  const rootElement = hasFloorPlan ? <EditorLayout /> : <FloorPlanLanding />;

  return (
    <Routes>
      <Route path="/" element={rootElement} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

/**
 * App - Main application component
 * Provides Redux store, routing, and error boundary
 */
const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </Provider>
    </ErrorBoundary>
  );
};

export default App;
